import React, { useEffect, useState, useRef } from 'react';

export default function SplashScreen({ onComplete }) {
  const [fadeOut, setFadeOut] = useState(false);
  const [isMusicMuted, setIsMusicMuted] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    // Create and play audio
    const audio = new Audio('/assets/audio/skyward-battalion.mp3');
    audio.loop = true;
    audio.volume = 0.5;
    
    // Try to play (some browsers require user interaction)
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.log('Audio autoplay prevented:', error);
      });
    }
    
    audioRef.current = audio;

    // Cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);
  
  // Mute/unmute audio when button clicked
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMusicMuted;
    }
  }, [isMusicMuted]);

  const handleClose = () => {
    // Stop music
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    // Fade out and complete
    setFadeOut(true);
    setTimeout(() => {
      if (onComplete) onComplete();
    }, 1000);
  };

  // ✅ NEW: Click anywhere (except buttons) to close
  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop (not the badge image or buttons)
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black transition-opacity duration-1000 cursor-pointer ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleBackdropClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* ✅ TOOLTIP: "Click to close" */}
      {showTooltip && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none z-40 animate-pulse">
          <div className="bg-black/90 text-white px-6 py-3 rounded-lg border-2 border-yellow-500 shadow-2xl">
            <div className="text-lg font-bold">👆 Click anywhere to close</div>
          </div>
        </div>
      )}
      
      {/* Splash Screen Image - Clickable to close */}
      <img 
        src="/assets/ui/splash-screen.png"
        alt="Global Thermo III"
        className="max-w-[90%] max-h-[90%] object-contain cursor-pointer hover:scale-105 transition-transform duration-300"
        onClick={handleClose}
        title="Click to close"
      />
      
      {/* Music Mute Button - Top Right */}
      <button
        onClick={(e) => {
          e.stopPropagation(); // Prevent backdrop click
          setIsMusicMuted(!isMusicMuted);
        }}
        className="absolute top-8 right-8 w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 z-50"
        style={{
          background: isMusicMuted 
            ? 'linear-gradient(135deg, #6b7280, #4b5563)' 
            : 'linear-gradient(135deg, #10b981, #059669)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}
        title={isMusicMuted ? "Unmute Music" : "Mute Music"}
      >
        <span className="text-2xl">
          {isMusicMuted ? '🔇' : '🔊'}
        </span>
      </button>
      
      {/* Battle Button - Optional (can still click badge or backdrop) */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-6">
        {/* Animated loading dots */}
        <div className="flex space-x-2">
          <div className="w-3 h-3 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-3 h-3 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
        
        {/* Battle button */}
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent backdrop click
            handleClose();
          }}
          className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold text-lg uppercase tracking-wider rounded-lg transition-all transform hover:scale-105 shadow-lg border-2 border-red-400"
          data-testid="start-battle-btn"
        >
          🎮 Click Here to Battle
        </button>
      </div>
    </div>
  );
}
